import _asyncToGenerator from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _toConsumableArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _regeneratorRuntime from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/regenerator";
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { getUseCase, RequestStatus } from '@lib/plugin-redux-core';
import { ADD_ATTACHMENTS } from '../redux/ai-chat/ai-chat.action';
import { AttachmentFileType } from '../enums';
import { CREATE_AI_ATTACHMENT_FILES_USE_CASE, VALIDATE_PUBLIC_FILE_URL_USE_CASE } from '../../../file-storage';
import { UPLOAD_CONVERSATION_FILES_USE_CASE } from '../../../ai';
import { UniqueEntityID } from '@digibricks/domains';
import { selectCurrentConversationId, selectCreateAiAttachmentFilesStatus, selectCreateAiAttachmentFilesOutput, selectValidatePublicFileUrlStatus, selectValidatePublicFileUrlOutput, selectUploadConversationFilesStatus, selectUploadConversationFilesOutput } from '../redux/ai-chat/ai-chat.selectors';

/** Derive AttachmentFileType from a file name or mime type. */
function getAttachmentFileType(fileName, mimeType) {
  var _mimeType$toLowerCase;

  var lower = fileName.toLowerCase();
  var mime = (_mimeType$toLowerCase = mimeType === null || mimeType === void 0 ? void 0 : mimeType.toLowerCase()) !== null && _mimeType$toLowerCase !== void 0 ? _mimeType$toLowerCase : '';
  if (lower.endsWith('.pdf') || mime === 'application/pdf') return AttachmentFileType.PDF_DOCUMENT;
  if (lower.endsWith('.docx') || lower.endsWith('.doc') || mime.includes('wordprocessingml')) return AttachmentFileType.WORD_DOCUMENT;
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv') || mime.includes('spreadsheet')) return AttachmentFileType.SPREADSHEET;
  if (/\.(png|jpg|jpeg|gif|webp|svg)$/.test(lower) || mime.startsWith('image/')) return AttachmentFileType.IMAGE;
  return AttachmentFileType.FILE;
}
/** Convert AttachmentFileType back to MIME type */


function getFileMimeTypeFromFileType(fileType) {
  switch (fileType) {
    case AttachmentFileType.PDF_DOCUMENT:
      return 'application/pdf';

    case AttachmentFileType.WORD_DOCUMENT:
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    case AttachmentFileType.SPREADSHEET:
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    case AttachmentFileType.IMAGE:
      return 'image/jpeg';

    default:
      return 'application/octet-stream';
  }
}
/** Map AttachmentInput[] → AttachmentItem[] */


function parseAttachmentInputsToAttachmentItems(inputs, sourceType) {
  return inputs.map(function (input) {
    return {
      id: input.id,
      name: input.fileName,
      url: input.url,
      fileType: getAttachmentFileType(input.fileName, input.fileMimeType),
      size: input.fileSize,
      type: sourceType
    };
  });
}

export var useUploadConversationFiles = function useUploadConversationFiles() {
  var dispatch = useDispatch();
  var currentConversationId = useSelector(selectCurrentConversationId);
  var createAiAttachmentFilesStatus = useSelector(selectCreateAiAttachmentFilesStatus);
  var createAiAttachmentFilesOutput = useSelector(selectCreateAiAttachmentFilesOutput);
  var validatePublicFileUrlStatus = useSelector(selectValidatePublicFileUrlStatus);
  var validatePublicFileUrlOutput = useSelector(selectValidatePublicFileUrlOutput);
  var uploadConversationFilesStatus = useSelector(selectUploadConversationFilesStatus);
  var uploadConversationFilesOutput = useSelector(selectUploadConversationFilesOutput);

  var _useState = useState(false),
      fileSelectorOpen = _useState[0],
      setFileSelectorOpen = _useState[1];

  var _useState2 = useState('upload'),
      fileSelectorTab = _useState2[0],
      setFileSelectorTab = _useState2[1];

  var _useState3 = useState(false),
      isSaving = _useState3[0],
      setIsSaving = _useState3[1];

  var _useState4 = useState([]),
      pendingFiles = _useState4[0],
      setPendingFiles = _useState4[1];

  var _useState5 = useState([]),
      pendingUrls = _useState5[0],
      setPendingUrls = _useState5[1];

  var _useState6 = useState([]),
      pendingAttachmentInputs = _useState6[0],
      setPendingAttachmentInputs = _useState6[1];

  var _useState7 = useState([]),
      validatedAttachmentInputs = _useState7[0],
      setValidatedAttachmentInputs = _useState7[1];

  var _useState8 = useState(null),
      validatingIndex = _useState8[0],
      setValidatingIndex = _useState8[1]; // Handle success of local file storage -> proceed to upload to conversation


  useEffect(function () {
    if (createAiAttachmentFilesStatus === RequestStatus.SUCCESS && createAiAttachmentFilesOutput) {
      if (!currentConversationId) {
        message.error('No active conversation found.');
        setIsSaving(false);
        return;
      }

      var attachmentInputs = createAiAttachmentFilesOutput.map(function (item, index) {
        var _file$name;

        var file = pendingFiles[index];
        return {
          id: item.id.toString(),
          fileName: (_file$name = file === null || file === void 0 ? void 0 : file.name) !== null && _file$name !== void 0 ? _file$name : 'file',
          fileMimeType: (file === null || file === void 0 ? void 0 : file.type) || 'application/pdf',
          url: item.url,
          fileSize: item.fileInformation.size
        };
      });
      setPendingAttachmentInputs(attachmentInputs);
      var uploadUseCase = getUseCase(UPLOAD_CONVERSATION_FILES_USE_CASE, dispatch);
      uploadUseCase.execute({
        conversationId: currentConversationId,
        files: attachmentInputs
      }); // Reset storage status

      getUseCase(CREATE_AI_ATTACHMENT_FILES_USE_CASE, dispatch).reset();
    }

    if (createAiAttachmentFilesStatus === RequestStatus.ERROR) {
      setIsSaving(false);
    }
  }, [createAiAttachmentFilesStatus, createAiAttachmentFilesOutput, currentConversationId, pendingFiles, dispatch]); // Handle URL validation progress -> proceed to upload to conversation when all urls are valid.

  useEffect(function () {
    if (validatePublicFileUrlStatus === RequestStatus.SUCCESS && validatePublicFileUrlOutput) {
      if (!currentConversationId) {
        message.error('No active conversation found.');
        setIsSaving(false);
        return;
      }
    }

    if (validatePublicFileUrlStatus === RequestStatus.ERROR) {
      message.error('Failed to validate one or more URLs. Please check and try again.');
      setIsSaving(false);
    }
  }, [validatePublicFileUrlStatus, validatePublicFileUrlOutput, currentConversationId]);
  useEffect(function () {
    if (validatingIndex === null || !validatePublicFileUrlOutput) {
      return;
    }

    if (validatePublicFileUrlStatus !== RequestStatus.SUCCESS) {
      return;
    }

    var currentUrl = pendingUrls[validatingIndex];

    if (!currentUrl) {
      return;
    }

    var nextInput = {
      id: new UniqueEntityID().toString(),
      fileName: validatePublicFileUrlOutput.fileName,
      fileMimeType: validatePublicFileUrlOutput.fileMimeType,
      url: currentUrl,
      fileSize: validatePublicFileUrlOutput.fileSize
    };
    var nextValidatedInputs = [].concat(_toConsumableArray(validatedAttachmentInputs), [nextInput]);
    setValidatedAttachmentInputs(nextValidatedInputs);
    getUseCase(VALIDATE_PUBLIC_FILE_URL_USE_CASE, dispatch).reset();
    var nextIndex = validatingIndex + 1;

    if (nextIndex < pendingUrls.length) {
      setValidatingIndex(nextIndex);
      getUseCase(VALIDATE_PUBLIC_FILE_URL_USE_CASE, dispatch).execute({
        url: pendingUrls[nextIndex]
      });
      return;
    }

    if (!currentConversationId) {
      message.error('No active conversation found.');
      setIsSaving(false);
      return;
    }

    setPendingAttachmentInputs(nextValidatedInputs);
    getUseCase(UPLOAD_CONVERSATION_FILES_USE_CASE, dispatch).execute({
      conversationId: currentConversationId,
      files: nextValidatedInputs
    });
    setValidatingIndex(null);
  }, [currentConversationId, dispatch, pendingUrls, validatePublicFileUrlOutput, validatePublicFileUrlStatus, validatedAttachmentInputs, validatingIndex]); // Handle success of final backend upload

  useEffect(function () {
    if (uploadConversationFilesStatus === RequestStatus.SUCCESS && uploadConversationFilesOutput) {
      var sourceType = pendingFiles.length > 0 ? 'LOCAL_FILE' : 'LINK';
      var attachments = parseAttachmentInputsToAttachmentItems(pendingAttachmentInputs, sourceType);
      dispatch({
        type: ADD_ATTACHMENTS,
        payload: attachments
      });
      setFileSelectorOpen(false);
      setIsSaving(false);
      setPendingFiles([]);
      setPendingUrls([]);
      setPendingAttachmentInputs([]);
      setValidatedAttachmentInputs([]);
      setValidatingIndex(null); // Reset upload status

      getUseCase(UPLOAD_CONVERSATION_FILES_USE_CASE, dispatch).reset();
    }

    if (uploadConversationFilesStatus === RequestStatus.ERROR) {
      setIsSaving(false);
    }
  }, [uploadConversationFilesStatus, uploadConversationFilesOutput, pendingFiles, pendingAttachmentInputs, dispatch]);
  var handleOpenUploadModal = useCallback(function (tab) {
    setFileSelectorTab(tab || 'upload');
    setFileSelectorOpen(true);
  }, []);
  var handleSave = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(data) {
      var uploadedFiles, urls, urlStrings, attachmentInputs;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (data.length) {
                _context.next = 3;
                break;
              }

              setFileSelectorOpen(false);
              return _context.abrupt("return");

            case 3:
              setIsSaving(true); // Handle uploaded files (type: 'LOCAL_FILE') and URLs (type: 'LINK')

              uploadedFiles = data.filter(function (item) {
                return item.type === 'LOCAL_FILE';
              });
              urls = data.filter(function (item) {
                return item.type === 'LINK';
              });

              if (!(urls.length > 0)) {
                _context.next = 15;
                break;
              }

              // Process URLs - they're already validated and ready to upload
              urlStrings = urls.map(function (url) {
                return url.url;
              });
              setPendingUrls(urlStrings);
              setPendingFiles([]);
              setValidatedAttachmentInputs([]);
              setValidatingIndex(0);
              getUseCase(VALIDATE_PUBLIC_FILE_URL_USE_CASE, dispatch).execute({
                url: urlStrings[0]
              });
              _context.next = 25;
              break;

            case 15:
              if (!(uploadedFiles.length > 0)) {
                _context.next = 25;
                break;
              }

              // For uploaded files, we need to create File objects from the attachment data
              // Since files are already uploaded, we can directly create attachment inputs
              attachmentInputs = uploadedFiles.map(function (item) {
                return {
                  id: item.id,
                  fileName: item.name,
                  fileMimeType: getFileMimeTypeFromFileType(item.fileType),
                  url: item.url,
                  fileSize: item.size || 0
                };
              });
              setPendingAttachmentInputs(attachmentInputs);
              setPendingFiles([]);
              setPendingUrls([]);

              if (currentConversationId) {
                _context.next = 24;
                break;
              }

              message.error('No active conversation found.');
              setIsSaving(false);
              return _context.abrupt("return");

            case 24:
              // Directly upload to conversation since files are already uploaded
              getUseCase(UPLOAD_CONVERSATION_FILES_USE_CASE, dispatch).execute({
                conversationId: currentConversationId,
                files: attachmentInputs
              });

            case 25:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [dispatch, currentConversationId]);
  return {
    fileSelectorOpen: fileSelectorOpen,
    fileSelectorTab: fileSelectorTab,
    isSaving: isSaving,
    handleOpenUploadModal: handleOpenUploadModal,
    handleSave: handleSave,
    setFileSelectorOpen: setFileSelectorOpen
  };
};